import '../../styles/restScreens.css';
import { useId, useMemo } from 'react';
import Button3D from '../ui/Button3D';
import { contactLocalTime, suggestContactTimeZone, timeZoneLabel, timeZoneOptions } from '../../lib/contactTimeZone';

// BL-177 Step 2j: the "Time zone" field of a CRM contact (edit card and Add Client).
//
// Pre-filled with the zone suggested from the PRIMARY address, then the phone. The moment a person picks a zone it is marked manual,
// and from then on auto-fill never touches it (contactTimeZone.timeZoneFieldsForSave + the database both honour that). "Use
// automatic" hands it back to auto-fill.
//
//   value / source   the draft's time_zone ('' = none stored) and time_zone_source ('auto' | 'manual' | null)
//   addresses/phones the draft's labeled entries, so the suggestion follows what is being typed
//   onChange         ({ time_zone, time_zone_source }) => void
const selectCls = 'w-full rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)] outline-none focus:border-brand-500';

export default function ContactTimeZoneField({ value = '', source = null, addresses = [], phones = [], onChange, disabled = false, idPrefix }) {
    const reactId = useId();
    const id = `${idPrefix || 'time-zone'}-${reactId}`;
    const manual = source === 'manual' && Boolean(value);
    const suggestion = useMemo(() => suggestContactTimeZone({ addresses, phones }), [addresses, phones]);
    // what a save would store: a person's choice, else the current suggestion (a zone nothing supports any more is dropped)
    const effective = manual ? value : (suggestion?.timeZone || '');
    const options = useMemo(() => timeZoneOptions(effective || null), [effective]);
    const local = contactLocalTime(effective);

    return (
        <div data-m6-layout="clients" data-m6-component="ContactTimeZoneField" data-testid="contact-time-zone-field">
            <select id={id} aria-label="Time zone" className={selectCls} value={effective} disabled={disabled}
                onChange={event => onChange?.(event.target.value
                    ? { time_zone: event.target.value, time_zone_source: 'manual' }
                    : { time_zone: '', time_zone_source: null })}>
                <option value="">{suggestion ? 'Automatic' : 'Not set'}</option>
                {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[var(--ink-muted)]" data-testid="contact-time-zone-hint">
                {manual
                    ? <span>Set by a person - auto-fill will not change it.</span>
                    : suggestion
                        ? <span>Suggested from the {suggestion.source === 'address' ? 'primary address' : 'phone number'}.</span>
                        : <span>Add an address or phone number and a zone is suggested.</span>}
                {local && <span>Their time now: {local}</span>}
                {manual && !disabled && (
                    <Button3D variant="pill" aria-label="Use automatic time zone"
                        onClick={() => onChange?.({ time_zone: suggestion?.timeZone || '', time_zone_source: suggestion ? 'auto' : null })}>
                        Use automatic
                    </Button3D>
                )}
            </div>
        </div>
    );
}

/** Read-only line for the contact card: "Eastern (ET) - their time now 9:41 AM". */
export function ContactTimeZoneText({ timeZone }) {
    if (!timeZone) return <span className="text-[var(--ink-muted)]">—</span>;
    const local = contactLocalTime(timeZone);
    return (
        <span data-testid="contact-time-zone-text">
            <span className="whitespace-nowrap">{timeZoneLabel(timeZone)}</span>
            {local && <span className="ml-2 whitespace-nowrap text-[11px] text-[var(--ink-muted)]">their time now {local}</span>}
        </span>
    );
}
